import { Link, NavLink, useNavigate } from "react-router-dom";
import { Home, Images, Heart, FolderHeart, Upload, LogOut, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import PetsnapLogo from "@/components/PetsnapLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { NAV, AUTH } from "@/constants/testIds";

export default function Nav({ onUpload }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const links = [
    { to: "/", label: "Home", icon: Home, id: NAV.dashboard },
    { to: "/gallery", label: "Gallery", icon: Images, id: NAV.gallery },
    { to: "/favorites", label: "Favorites", icon: Heart, id: NAV.favorites },
    { to: "/albums", label: "Albums", icon: FolderHeart, id: NAV.albums },
  ];

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="sticky top-0 z-30 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#EAE2D6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 shrink-0">
          <PetsnapLogo className="w-10 h-10" />
          <span className="font-display text-2xl font-bold text-[#2D232E] hidden sm:inline">Petsnap</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 bg-white/60 rounded-full border border-[#EAE2D6] px-1.5 py-1">
          {links.map(({ to, label, icon: Icon, id }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={id}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold transition-all ${
                  isActive
                    ? "bg-[#E76F51] text-white shadow-sm"
                    : "text-[#6A606C] hover:text-[#2D232E] hover:bg-white"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/pricing"
            data-testid="nav-pricing"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#2D232E] hover:bg-[#3a2f3c] text-white font-semibold h-10 px-4 text-sm transition-transform hover:scale-105"
          >
            <Sparkles className="w-4 h-4 text-[#E9C46A]" />
            Pro
          </Link>
          <Button
            data-testid={NAV.uploadBtn}
            onClick={onUpload}
            className="rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold h-10 px-4 shadow-[0_6px_20px_rgba(231,111,81,0.25)] transition-transform hover:scale-105"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            Upload
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={NAV.userMenu} className="focus:outline-none">
                <Avatar className="w-10 h-10 border-2 border-[#EAE2D6] cursor-pointer hover:border-[#E76F51] transition-colors">
                  <AvatarFallback className="bg-[#E9C46A] text-[#2D232E] font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-2xl border-[#EAE2D6] bg-white shadow-[0_8px_30px_rgba(45,35,46,0.12)]">
              <DropdownMenuLabel className="font-display">
                <div className="text-[#2D232E] font-semibold">{user?.name}</div>
                <div className="text-xs text-[#6A606C] font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#EAE2D6]" />
              <DropdownMenuItem
                data-testid={AUTH.logoutBtn}
                onClick={async () => {
                  await logout();
                  navigate("/auth");
                }}
                className="rounded-xl focus:bg-[#F4EFE6] focus:text-[#2D232E] cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile bottom-ish nav */}
      <nav className="md:hidden flex items-center justify-around bg-white/80 border-t border-[#EAE2D6] py-2">
        {links.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-xs font-semibold ${
                isActive ? "text-[#E76F51]" : "text-[#6A606C]"
              }`
            }
          >
            <Icon className="w-5 h-5" />
            {label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
